import '../operations.css';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { campaignsApi } from '../../api/campaigns';
import type { CreateCampaignRequest, RecurrenceInterval } from '../../api/campaigns';
import { templatesApi } from '../../api/templates';
import { groupsApi } from '../../api/groups';
import { pagesApi } from '../../api/pages';
import { smtpApi, warmupStatusLine } from '../../api/smtp';
import { suppressionsApi } from '../../api/suppressions';
import { smsProvidersApi } from '../../api/smsProviders';
import { voiceProvidersApi } from '../../api/voiceProviders';
import type { Template, Group, Page, SMTP, CampaignChannel, SMSProvider, VoiceProvider } from '../../types';
import { AiCampaignDesigner } from './AiCampaignDesigner';

export function CampaignCreate() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    template_id: 0,
    page_id: 0,
    smtp_id: 0,
    sms_provider_id: 0,
    voice_provider_id: 0,
    group_ids: [] as number[],
    url: '',
    template_ids: [] as number[],
    no_whitelist: false,
    recurring: false,
    recurrence_interval: 'weekly' as RecurrenceInterval,
    recurrence_end_date: '',
    channel: 'email' as CampaignChannel,
    channel_config: '',
  });
  const [urlDraft, setUrlDraft] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [testRecipient, setTestRecipient] = useState('');
  const [preflightOk, setPreflightOk] = useState(false);
  const [testOk, setTestOk] = useState(false);
  const [deliveryNote, setDeliveryNote] = useState<string | null>(null);
  const [deliveryBusy, setDeliveryBusy] = useState(false);

  const { data: trackingURL } = useQuery({
    queryKey: ['campaign-tracking-url'],
    queryFn: async () => (await campaignsApi.trackingURL()).data.url,
    retry: false,
  });
  const campaignURL = urlDraft ?? trackingURL ?? '';

  const { data: suppressions } = useQuery({
    queryKey: ['suppressions'],
    queryFn: async () => (await suppressionsApi.getAll()).data,
    retry: false,
  });

  const { data: smtpHealth } = useQuery({
    queryKey: ['smtp-health', formData.smtp_id],
    queryFn: async () => (await smtpApi.getHealth(formData.smtp_id)).data,
    enabled: formData.channel === 'email' && formData.smtp_id > 0,
    retry: false,
  });

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => {
      const res = await templatesApi.getAll();
      return res.data;
    },
  });

  const { data: groups } = useQuery({
    queryKey: ['groups'],
    queryFn: async () => {
      const res = await groupsApi.getAll();
      return res.data;
    },
  });

  const { data: pages } = useQuery({
    queryKey: ['pages'],
    queryFn: async () => {
      const res = await pagesApi.getAll();
      return res.data;
    },
  });

  const { data: smtps } = useQuery({
    queryKey: ['smtps'],
    queryFn: async () => {
      const res = await smtpApi.getAll();
      return res.data;
    },
  });

  const { data: smsProviders } = useQuery({
    queryKey: ['smsProviders'],
    queryFn: async () => {
      const res = await smsProvidersApi.getAll();
      return res.data;
    },
  });

  const { data: voiceProviders } = useQuery({
    queryKey: ['voiceProviders'],
    queryFn: async () => {
      const res = await voiceProvidersApi.getAll();
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateCampaignRequest) => campaignsApi.create(payload),
    onSuccess: () => {
      navigate('/campaigns');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    const template = templates?.find((t) => t.id === formData.template_id);
    const page = pages?.find((p) => p.id === formData.page_id);
    const smtp = smtps?.find((s) => s.id === formData.smtp_id);
    const smsProvider = smsProviders?.find((p) => p.id === formData.sms_provider_id);
    const voiceProvider = voiceProviders?.find((p) => p.id === formData.voice_provider_id);
    const selectedGroups = (groups ?? []).filter((g) => formData.group_ids.includes(g.id));

    const templateRequired = ['email', 'sms', 'callback'].includes(formData.channel);
    const pageRequired = ['email', 'callback', 'qr', 'usb_drop'].includes(formData.channel) ||
      (formData.channel === 'sms' && !!template?.text?.includes('{{.URL'));
    if ((templateRequired && !template) || (pageRequired && !page) || (page && !campaignURL.trim())) {
      setFormError(templateRequired && pageRequired ? 'Select a template, landing page, and tracking URL.' :
        pageRequired ? 'Select a landing page and tracking URL.' : 'A tracking URL is required when a landing page is selected.');
      return;
    }
    if (selectedGroups.length === 0) {
      setFormError('Select at least one target group.');
      return;
    }
    if ((formData.channel === 'email' || formData.channel === 'callback') && !smtp) {
      setFormError('Select an SMTP sending profile for email and callback campaigns.');
      return;
    }
    if (formData.channel === 'email' && (!preflightOk || !testOk)) {
      setFormError('Run a successful preflight and send a test email before launching an email campaign.');
      return;
    }
    if (formData.channel === 'sms' && !smsProvider) {
      setFormError('Select an SMS provider for SMS campaigns.');
      return;
    }
    if (formData.channel === 'voice' && !voiceProvider) {
      setFormError('Select a voice provider for voice campaigns.');
      return;
    }

    // Go PostCampaign resolves nested { name } objects (legacy engine shape);
    // template/page/smtp/group IDs are json:"-" and ignored by the decoder.
    const channelConfig: Record<string, number> = {};
    if (formData.channel === 'sms' && smsProvider) channelConfig.sms_provider_id = smsProvider.id;
    if (formData.channel === 'voice' && voiceProvider) channelConfig.voice_provider_id = voiceProvider.id;

    const payload: CreateCampaignRequest = {
      name: formData.name,
      ...(template ? { template: { name: template.name } } : {}),
      ...(page ? { page: { name: page.name } } : {}),
      smtp: { name: smtp?.name ?? '' },
      groups: selectedGroups.map((g) => ({ name: g.name })),
      url: campaignURL,
      channel: formData.channel,
      channel_config: JSON.stringify(channelConfig),
      ...(template ? { template_ids: formData.template_ids } : {}),
      no_whitelist: formData.no_whitelist,
      ...(formData.recurring
        ? {
            recurring: true,
            recurrence_interval: formData.recurrence_interval,
            ...(formData.recurrence_end_date
              ? { recurrence_end_date: new Date(formData.recurrence_end_date).toISOString() }
              : {}),
          }
        : {}),
    };
    createMutation.mutate(payload);
  };

  const markSmtpChanged = (smtpId: number) => {
    setPreflightOk(false);
    setTestOk(false);
    setDeliveryNote(null);
    setFormData({ ...formData, smtp_id: smtpId });
  };

  const runPreflight = async () => {
    if (formData.smtp_id <= 0) return;
    setDeliveryBusy(true);
    setDeliveryNote(null);
    try {
      const result = await smtpApi.runPreflight(formData.smtp_id);
      setPreflightOk(result.data.smtp_ok);
      setDeliveryNote(result.data.smtp_ok ? 'Preflight passed.' : result.data.smtp_error || 'Preflight failed.');
    } catch {
      setPreflightOk(false);
      setDeliveryNote('Preflight failed.');
    } finally {
      setDeliveryBusy(false);
    }
  };

  const sendTest = async () => {
    const template = templates?.find((item) => item.id === formData.template_id);
    const page = pages?.find((item) => item.id === formData.page_id);
    const smtp = smtps?.find((item) => item.id === formData.smtp_id);
    if (!template || !smtp || !testRecipient.trim() || !campaignURL.trim()) {
      setDeliveryNote('Choose a template, landing page URL, and test recipient first.');
      return;
    }
    setDeliveryBusy(true);
    setDeliveryNote(null);
    try {
      await smtpApi.sendCampaignTest({
        templateName: template.name,
        pageName: page?.name,
        smtpName: smtp.name,
        url: campaignURL,
        email: testRecipient.trim(),
      });
      setTestOk(true);
      setDeliveryNote(`Test email sent to ${testRecipient.trim()}.`);
    } catch {
      setTestOk(false);
      setDeliveryNote('Test email failed.');
    } finally {
      setDeliveryBusy(false);
    }
  };

  const handleGroupToggle = (groupId: number) => {
    setFormData((prev) => ({
      ...prev,
      group_ids: prev.group_ids.includes(groupId)
        ? prev.group_ids.filter((id) => id !== groupId)
        : [...prev.group_ids, groupId],
    }));
  };

  const handleVariantToggle = (templateId: number) => {
    setFormData((prev) => ({
      ...prev,
      template_ids: prev.template_ids.includes(templateId)
        ? prev.template_ids.filter((id) => id !== templateId)
        : [...prev.template_ids, templateId],
    }));
  };

  const suppressionEntries = Array.isArray(suppressions) ? suppressions : [];
  const suppressedEmails = new Set(suppressionEntries.map((entry) => entry.email.toLowerCase()));
  const skippedRecipients = (groups ?? [])
    .filter((group) => formData.group_ids.includes(group.id))
    .flatMap((group) => group.targets ?? [])
    .filter((target) => suppressedEmails.has((target.email ?? '').toLowerCase())).length;

  return (
    <div className="operations-page max-w-3xl mx-auto space-y-6">
      <div>
        <p className="operations-eyebrow">Simulation workspace</p>
          <h1 className="text-2xl font-bold text-gray-900">Create Campaign</h1>
        <p className="text-gray-500 mt-1">Set up a new phishing simulation</p>
      </div>

      <AiCampaignDesigner
        onApplyName={(name) => setFormData((prev) => ({ ...prev, name }))}
        onApplyTemplate={async (subject, html, text) => {
          const name = subject.trim() || 'AI lure';
          const created = (await templatesApi.create({ name, subject: subject.trim() || name, html, text })).data;
          queryClient.setQueryData<Template[]>(['templates'], (current) => {
            const list = Array.isArray(current) ? current.filter((item) => item.id !== created.id) : [];
            return [...list, created];
          });
          setFormData((prev) => ({ ...prev, template_id: created.id, name: prev.name.trim() ? prev.name : name }));
          setFormError(null);
        }}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h3>
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="campaign-name">Campaign Name</label>
              <input
                type="text"
                id="campaign-name"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Q4 Security Awareness Training"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="campaign-url">Campaign URL{['sms', 'voice'].includes(formData.channel) ? ' (optional for link-free exercises)' : ''}</label>
              <input
                type="url"
                id="campaign-url"
                className="input"
                value={campaignURL}
                onChange={(e) => setUrlDraft(e.target.value)}
                placeholder="https://training.example.com"
                required={['email', 'callback', 'qr', 'usb_drop'].includes(formData.channel) || !!pages?.find((p: Page) => p.id === formData.page_id)}
              />
              <p className="text-sm text-gray-500 mt-1">The URL targets will see</p>
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Channel</h3>
          <div className="space-y-4">
            <div>
              <label className="label" htmlFor="campaign-channel">Simulation channel</label>
              <select
                className="input"
                id="campaign-channel"
                value={formData.channel}
                onChange={(e) =>
                  setFormData({ ...formData, channel: e.target.value as CampaignChannel })
                }
              >
                <option value="email">Email</option>
                <option value="sms">SMS / Smishing</option>
                <option value="voice">Voice / Vishing</option>
                <option value="qr">QR code / Quishing</option>
                <option value="usb_drop">USB drop</option>
                <option value="callback">Callback / TOAD</option>
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Delivery channel for the simulation. Non-email channels skip the SMTP server and
                use channel-specific providers instead.
              </p>
            </div>

            {(formData.channel === 'email' || formData.channel === 'callback') && (
              <div>
                <label className="label">SMTP Sending Profile</label>
                <select
                  className="input"
                  value={formData.smtp_id}
                  onChange={(e) => markSmtpChanged(Number(e.target.value))}
                >
                  <option value={0}>Select a sending profile</option>
                  {smtps?.map((smtp: SMTP) => (
                    <option key={smtp.id} value={smtp.id}>
                      {smtp.name} ({smtp.host})
                    </option>
                  ))}
                </select>
                {formData.channel === 'email' && formData.smtp_id > 0 && (
                  <div className="mt-3 space-y-3">
                    {smtpHealth && warmupStatusLine(smtpHealth) && (
                      <p className="text-sm text-gray-600">{warmupStatusLine(smtpHealth)}</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="sm" disabled={deliveryBusy} onClick={runPreflight}>Run preflight</Button>
                    </div>
                    <label className="label" htmlFor="campaign-test-recipient">Test recipient</label>
                    <div className="flex flex-wrap gap-2">
                      <input id="campaign-test-recipient" className="input max-w-sm" type="email" value={testRecipient} onChange={(e) => { setTestRecipient(e.target.value); setTestOk(false); }} placeholder="you@example.com" />
                      <Button type="button" variant="secondary" size="sm" disabled={deliveryBusy} onClick={sendTest}>Send test email</Button>
                    </div>
                    {deliveryNote && <p className="text-sm text-gray-600">{deliveryNote}</p>}
                  </div>
                )}
              </div>
            )}

            {formData.channel === 'sms' && (
              <div>
                <label className="label" htmlFor="campaign-sms-provider">SMS Provider</label>
                <select
                  id="campaign-sms-provider"
                  aria-label="SMS Provider"
                  className="input"
                  value={formData.sms_provider_id}
                  onChange={(e) =>
                    setFormData({ ...formData, sms_provider_id: Number(e.target.value) })
                  }
                >
                  <option value={0}>Select an SMS provider</option>
                  {smsProviders?.map((p: SMSProvider) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.provider_type})
                    </option>
                  ))}
                </select>
              <p className="text-sm text-gray-500 mt-1">SMS recipients use the target phone number. Older groups may use the legacy address field.</p>
              </div>
            )}

            {formData.channel === 'voice' && (
              <div>
                <label className="label">Voice Provider</label>
                <select
                  className="input"
                  value={formData.voice_provider_id}
                  onChange={(e) =>
                    setFormData({ ...formData, voice_provider_id: Number(e.target.value) })
                  }
                >
                  <option value={0}>Select a voice provider</option>
                  {voiceProviders?.map((p: VoiceProvider) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.provider_type})
                    </option>
                  ))}
                </select>
                <p className="text-sm text-gray-500 mt-1">The provider supplies the voice script. If it uses {'{{.URL}}'}, select a landing page and tracking URL.</p>
              </div>
            )}

            {(formData.channel === 'qr' || formData.channel === 'usb_drop') && (
              <p className="text-sm text-gray-500">
                For <strong>{formData.channel.replace('_', ' ')}</strong> campaigns the simulation
                URL is attributed automatically. Configure the landing page below; these exercises
                do not require an email or SMS template.
              </p>
            )}
          </div>
        </Card>

        {['email', 'sms', 'callback'].includes(formData.channel) && <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{formData.channel === 'sms' ? 'SMS text template' : 'Email Template'}</h3>
          <select
            className="input"
            aria-label={formData.channel === 'sms' ? 'SMS text template' : 'Email template'}
            value={formData.template_id}
            onChange={(e) => setFormData({ ...formData, template_id: Number(e.target.value) })}
            required
          >
            <option value={0}>Select a template</option>
            {templates?.filter((template: Template) => formData.channel !== 'sms' || !!template.text?.trim()).map((template: Template) => (
              <option key={template.id} value={template.id}>
                {template.name}
              </option>
            ))}
          </select>
        </Card>}

        {formData.channel === 'email' && <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Template variants (random per recipient)
          </h3>
          <p className="text-sm text-gray-500 mb-3">
            Optional. When set, each recipient is randomly assigned one of these templates so
            unwhitelisted sends don't present identical HTML to mail gateway fingerprinting. The
            template selected above remains the fallback.
          </p>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {templates?.map((template: Template) => (
              <label
                key={template.id}
                className="flex items-center gap-3 p-3 break-words min-w-0 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.template_ids.includes(template.id)}
                  onChange={() => handleVariantToggle(template.id)}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-900">{template.name}</p>
                  <p className="text-sm text-gray-500">{template.subject}</p>
                </div>
              </label>
            ))}
          </div>
          <label className="flex items-center gap-3 cursor-pointer mt-4">
            <input
              type="checkbox"
              checked={formData.no_whitelist}
              onChange={(e) => setFormData({ ...formData, no_whitelist: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
            />
            <div>
              <p className="font-medium text-gray-900">Unwhitelisted send (true-stack test)</p>
              <p className="text-sm text-gray-500">
                Labels this campaign as delivered through unwhitelisted/lookalike domains so
                analytics don't mix its metrics with whitelisted sends
              </p>
            </div>
          </label>
        </Card>}

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Landing Page{['sms', 'voice'].includes(formData.channel) ? ' (optional)' : ''}</h3>
          <select
            className="input"
            aria-label="Landing Page"
            value={formData.page_id}
            onChange={(e) => setFormData({ ...formData, page_id: Number(e.target.value) })}
            required
          >
            <option value={0}>{['sms', 'voice'].includes(formData.channel) ? 'No landing page' : 'Select a landing page'}</option>
            {pages?.map((page: Page) => (
              <option key={page.id} value={page.id}>
                {page.name}
              </option>
            ))}
          </select>
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recurrence</h3>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.recurring}
              onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
              className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
            />
            <div>
              <p className="font-medium text-gray-900">Recurring campaign</p>
              <p className="text-sm text-gray-500">
                Automatically launch a new occurrence of this campaign on a schedule
              </p>
            </div>
          </label>
          {formData.recurring && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="label">Repeat every</label>
                <select
                  className="input"
                  value={formData.recurrence_interval}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      recurrence_interval: e.target.value as RecurrenceInterval,
                    })
                  }
                >
                  <option value="daily">Day</option>
                  <option value="weekly">Week</option>
                  <option value="monthly">Month</option>
                </select>
              </div>
              <div>
                <label className="label">Ends on (optional)</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={formData.recurrence_end_date}
                  onChange={(e) =>
                    setFormData({ ...formData, recurrence_end_date: e.target.value })
                  }
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave blank to repeat until the campaign is completed or deleted
                </p>
              </div>
            </div>
          )}
        </Card>

        <Card>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Target Groups</h3>
          {formData.channel === 'email' && formData.group_ids.length > 0 && (
            <p className="text-sm text-gray-600 mb-3">{skippedRecipients} selected recipient{skippedRecipients === 1 ? '' : 's'} will be skipped because they are suppressed.</p>
          )}
          <div className="space-y-2">
            {groups?.map((group: Group) => (
              <label
                key={group.id}
                className="flex items-center gap-3 p-3 break-words min-w-0 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={formData.group_ids.includes(group.id)}
                  onChange={() => handleGroupToggle(group.id)}
                  className="w-4 h-4 text-indigo-600 rounded border-gray-300 focus:ring-indigo-500"
                />
                <div>
                  <p className="font-medium text-gray-900">{group.name}</p>
                  <p className="text-sm text-gray-500">{group.targets?.length ?? 0} targets</p>
                </div>
              </label>
            ))}
          </div>
        </Card>

        {(formError || createMutation.isError) && (
          <p role="alert" className="text-red-700">
            {formError ?? 'Could not create the campaign. Check your selections and try again.'}
          </p>
        )}

        <div className="flex flex-wrap justify-end gap-3">
          <Button type="button" variant="secondary" onClick={() => navigate('/campaigns')}>
            Cancel
          </Button>
          <Button type="submit" loading={createMutation.isPending}>
            Launch Campaign
          </Button>
        </div>
      </form>
    </div>
  );
}
